import {
  BodyHttpMethodStrategy,
  IHttpMethodStrategy,
  NonBodyBlobHttpMethodStrategy,
  BlobDownloadHttpMethodStrategy,
  NonBodyHttpMethodStrategy
} from "./httpMethodStrategies";
import {AuthenticationHandler, IAuthenticationHandler} from "../authenticationHandler/authenticationHandler";
import {ApiRequestMethod} from "../generics/apiRequestMethod";
import {IInterceptor, Interceptor} from "../interceptor/interceptor";
import {HttpCodes} from "../generics/httpCodes";

export interface IApiServiceHandler {
  authenticationHandler: IAuthenticationHandler;
  headers: Headers;

  api: (
    url: string,
    method: ApiRequestMethod,
    body?: any,
    retry?: boolean
  ) => Promise<any>;
}

export class ApiServiceHandler implements IApiServiceHandler {
  private readonly baseURL: string;
  private strategyMap: Map<ApiRequestMethod, { new (): IHttpMethodStrategy }>;
  headers: Headers;
  public interceptor: IInterceptor;
  public authenticationHandler: IAuthenticationHandler;

  constructor(
    baseURL: string,
    accessTokenEndPoint: string,
    refreshTokenEndPoint: string,
    interceptor: IInterceptor = new Interceptor(),
    accessTokenKey: string = "access",
    refreshTokenKey: string = "refresh",
    authenticationHandler: IAuthenticationHandler = new AuthenticationHandler(
      baseURL,
      accessTokenEndPoint,
      refreshTokenEndPoint,
      accessTokenKey,
      refreshTokenKey
    )
  ) {
    this.baseURL = baseURL;
    this.strategyMap = new Map()
      .set("POST", BodyHttpMethodStrategy)
      .set("PUT", BodyHttpMethodStrategy)
      .set("PATCH", BodyHttpMethodStrategy)
      .set("GET_BLOB", NonBodyBlobHttpMethodStrategy)
      .set("DOWNLOAD_BLOB", BlobDownloadHttpMethodStrategy)
      .set("GET", NonBodyHttpMethodStrategy)
      .set("DELETE", NonBodyHttpMethodStrategy)
      .set("OPTIONS", NonBodyHttpMethodStrategy);
    this.headers = new Headers();
    this.interceptor = interceptor;
    this.authenticationHandler = authenticationHandler;
    this.authenticationHandler.apiService = this;
  }

  async api(
    url: string,
    method: ApiRequestMethod,
    body: any = null,
    retry: boolean = false
  ): Promise<any> {
    try {
      const originalUrl = url.includes(this.baseURL) ? url : this.baseURL + url;
      const strategyClass =
        this.strategyMap.get(method) || NonBodyHttpMethodStrategy;
      const strategy = new strategyClass();
      await this.interceptor.beforeRequest();
      const response = await strategy.execute(
        originalUrl,
        method,
        body,
        this.headers
      );
      await this.interceptor.afterResponse(response);
      return response.data;
    } catch (e: unknown) {
      const knownError = e as Response;
      if (retry) {
        return;
      } else if (knownError?.status === HttpCodes.HTTP_UNAUTHORIZED) {
        await this.authenticationHandler.reauthenticate();
        const retryFlag = true;
        return await this.api(url, method, body, retryFlag);
      } else {
        await this.interceptor.afterError(e);
      }
      throw e;
    }
  }
}
